import type { Scope, SiteUse } from './sites';
import { z } from 'zod';
import type { BuildMessageDto } from './builder';
import { COMPANY_KEY_FACTS, type CompanyKeyFact, type EquipmentObligation, type EquipmentStatus } from './company';
import type { LibraryCoverageDto, LibraryGround, LibraryRefDto } from './library-refs';
import type { AccountRefDto } from './accounts';

/** Onboarding conversazionale: il sistema conosce l'azienda parlando, e ne ricava profilo, obblighi e processi. */

export interface CompanyFact {
  sezione: string;
  voce: string;
  valore: string;
  /** documento = da un documento dell'azienda (caricato o da un programma collegato): vale come detto dalla persona. */
  fonte: 'web' | 'conversazione' | 'documento';
  /** Nome del documento da cui viene, per fonte documento. */
  documento?: string;
  certezza: 'confermato' | 'da_confermare';
  aggiornato: string;
}

export interface CompanyTheme {
  tema: string;
  stato: 'da_esplorare' | 'parziale' | 'coperto';
  nota: string;
}

export interface CompanyRole {
  chiave: string;
  nome: string;
  area: string;
  responsabilita: string[];
  /** proposto = ragionamento del motore; sito / conversazione = dato dell'azienda. */
  fonte: 'proposto' | 'sito' | 'conversazione' | 'documento';
  stato: 'proposto' | 'confermato';
}

/** Ruolo tolto dalla verifica dei ruoli (doppione, figura che l'azienda non ha o di un solo progetto). */
export interface RemovedRoleDto {
  chiave: string;
  nome: string;
  motivo: string;
  /** Il ruolo che ne ha preso il posto (persone e bozze sono passate a lui); null se è uscito e basta. */
  sostituito_da: string | null;
}

export interface CompanyObligation {
  chiave: string;
  area?: string;
  norma: string;
  descrizione: string;
  perche: string;
  prova: string;
  scadenza: string | null;
  certezza: 'confermato' | 'da_confermare';
  /** Le cose dell'azienda a cui si applica (es. un luogo, un bene), con il loro nome; vuoto = a tutta l'azienda. */
  riguarda?: string[];
  /** Per quali sedi vale (ADR 0017); assente = tutta l'azienda. */
  ambito?: Scope;
}

export interface ProcessCandidateDto {
  id: string;
  key: string;
  name: string;
  area: string | null;
  description: string | null;
  details: {
    trigger?: string;
    ruoli?: string[];
    frequenza?: string;
    passi?: string[];
    informazioni?: string[];
    documenti?: string[];
    normative?: string[];
    software?: string[];
    /** Le cose dell'azienda su cui si svolge (es. un luogo, un bene); vuoto = tutta l'azienda. */
    riguarda?: string[];
    /** raccontato = descritto dalla persona; proposto = ricavato dal motore da com'è fatta l'azienda (da confermare). */
    origine?: 'raccontato' | 'proposto';
    /** ricorrente, periodico (riunioni, report, verifiche), evento (quando succede qualcosa), adempimento. */
    tipo?: string;
    /** Come è stata gestita davvero (dalla posta dell'azienda): fonte, nota, quando. */
    evidenze?: Array<{ fonte: string; nota: string; il: string }>;
    /** Perché è stata tolta dal sistema: una_tantum = il motore l'ha giudicata un'attività fatta una volta sola. */
    scartato?: 'una_tantum';
    scartato_motivo?: string;
    /** La persona l'ha ripristinata: è ricorrente e il motore non la toglie più come una tantum. */
    ricorrente?: boolean;
  };
  status: 'individuato' | 'pronto' | 'in_elaborazione' | 'bozza_pronta' | 'adottato' | 'scartato';
  /** Per quali sedi vale (ADR 0017). */
  ambito: Scope;
  conversation_id: string | null;
  process_id: string | null;
  error: string | null;
  updated_at: string;
}

/** Elenco numerato dei processi di un'area da verificare con la persona (costruito dal sistema: non si salta nulla). */
export interface ReviewCard {
  area: string;
  numero_area: number;
  totale_aree: number;
  items: Array<{
    n: number;
    key: string;
    name: string;
    description: string | null;
    stato: 'da_verificare' | 'confermato' | 'scartato';
  }>;
}

export interface OnboardingMessageDto {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  questions: Array<{ question: string; why: string }>;
  changes: {
    fatti?: number;
    processi?: string[];
    obblighi?: number;
    ruoli?: string[];
    verifica?: ReviewCard;
    allegati?: Array<{ id: string; titolo: string }>;
    /** Di cosa parla il messaggio nel percorso (es. "Area Vendite · 2 di 6"), per la conversazione principale. */
    passo?: string;
    /** Il passo della configurazione guidata a cui risponde il messaggio del cervello (ADR 0063): «Parliamone» mostra i suoi. */
    chiave?: WizardStepKey;
    /** Cosa ha cambiato la persona con questo messaggio nei passi della configurazione, passo per passo (ADR 0052). */
    aggiornati?: Array<{ passo: WizardStepKey; cosa: string[] }>;
    /** Il passo da cui ha scritto la persona si è riprogettato con la sua decisione (ADR 0082). */
    riprogettato?: boolean;
  };
  software: BuildMessageDto['software'];
  actions: BuildMessageDto['actions'];
  sources: string[];
  created_at: string;
}

export interface ActivityDto {
  started_at: string;
  steps: Array<{ label: string; done: boolean }>;
}

export interface OnboardingDocumentDto {
  id: string;
  name: string;
  path: string;
  size: number;
  origin: string;
  status: 'in_coda' | 'in_lettura' | 'letto' | 'non_leggibile' | 'errore';
  summary: {
    tipo?: string;
    /** Di chi è il documento (società, persona o immobile), se risulta. */
    intestatario?: string | null;
    /** altro_soggetto: documento di un'altra società o persona, non ne entra nulla nella conoscenza dell'azienda. */
    riguarda?: 'azienda' | 'altro_soggetto';
    /** false per bozze, testi da valutare, modelli da compilare. */
    definitivo?: boolean;
    riassunto?: string;
    fatti?: number;
    processi?: string[];
    ruoli?: number;
    persone?: number;
    software?: string[];
    corrisponde_a?: string | null;
    /** Data scritta nel documento (AAAA-MM-GG), se c'è. */
    data?: string | null;
    /** Da dove arriva, quando lo dice la fonte (es. l'allegato di quale mail): lo collega al resto. */
    provenienza?: string | null;
  };
  /** A quale sede si riferisce (ADR 0017). */
  ambito: Scope;
  error: string | null;
  created_at: string;
}

/** Come lavora un'area (dalla chat principale): con quali programmi o a mano; completata quando non c'è altro da chiedere. */
export interface AreaTools {
  stato: 'da_chiedere' | 'in_corso' | 'completata';
  /** Programmi, archivi, caselle di posta… o "a mano", in una frase. */
  modo: string;
}

/** Un'area della pagina Procedure: procedure standard del settore da verificare nella sua chat di approfondimento. */
export interface AreaSummaryDto {
  n: number;
  area: string;
  stato: 'da_verificare' | 'approfondimento' | 'completata';
  procedure: number;
  confermate: number;
  da_verificare: number;
  /** Come lavora l'area oggi, se registrato (configurazione operativa, ADR 0041): la configurazione guidata non lo chiede. */
  strumenti: AreaTools | null;
  messaggi: number;
  thinking: boolean;
}

/** Stato di un passo della configurazione o di una fase di un'area. */
export type SetupStatus = 'da_fare' | 'in_corso' | 'parziale' | 'fatto';

/** Un passo del percorso di configurazione dell'azienda, nell'ordine in cui conviene farli. */
export interface SetupStepDto {
  chiave: 'azienda' | 'sedi' | 'aree' | 'struttura' | 'verifica' | 'approfondimento' | 'documenti' | 'persone' | 'collegamenti';
  titolo: string;
  /** Cosa si fa in questo passo, in una frase. */
  descrizione: string;
  stato: SetupStatus;
  fatti: number;
  totale: number;
  /** Dove si è arrivati, in parole. */
  dettaglio: string;
  /** Sezione dell'app dove si fa (relativa all'azienda, es. "procedure/2"). */
  link: string;
  /** Si può fare in qualsiasi momento, senza aspettare i passi prima. */
  sempre: boolean;
}

/** Un'area della mappa con lo stato di ogni fase, nella chat dell'area: verifica e approfondimento (ADR 0041). */
export interface SetupAreaDto {
  n: number;
  area: string;
  stato: SetupStatus;
  /** Comparsa con l'ultimo aggiornamento della mappa e non ancora toccata. */
  nuova: boolean;
  verifica: { stato: SetupStatus; confermate: number; da_verificare: number };
  approfondimento: { stato: SetupStatus };
}

/** Una cosa che il sistema non sa e che conta: la domanda, perché conta e cosa cambierebbe. */
export interface BlindSpotDto {
  domanda: string;
  perche: string;
  /** Obblighi, rischi o procedure che dipendono dalla risposta. */
  cosa_cambia: string;
  /** 1 = da sapere subito, 3 = utile. */
  priorita: 1 | 2 | 3;
  area: string | null;
}

/** Percorso di configurazione: passi in ordine, aree (dinamiche) con le loro fasi e il prossimo passo da fare. */
export interface SetupProgressDto {
  percentuale: number;
  passi: SetupStepDto[];
  aree: SetupAreaDto[];
  /** Aree tolte dall'ultimo aggiornamento della mappa (le loro procedure restano, tra le altre). */
  aree_tolte: string[];
  aree_cambiate_il: string | null;
  /**
   * Cose che il sistema non sa ancora e che cambierebbero obblighi, rischi o procedure (punti ciechi), in ordine di
   * importanza: le chiede la conversazione principale al momento giusto.
   */
  da_sapere: BlindSpotDto[];
  /** Una cosa sola da fare adesso; null quando la configurazione è completa. */
  prossimo: { passo: SetupStepDto['chiave']; testo: string; link: string } | null;
  /** I passi del wizard (ADR 0022), in ordine, con il passo consigliato (il primo non fatto). */
  wizard: WizardStepDto[];
  /** Quanto è fatta la configurazione guidata, solo dai suoi passi (ADR 0040): 100 quando sono tutti fatti. */
  percentuale_guidata: number;
  consigliato: WizardStepKey | null;
  /**
   * Il motore AI è fermo per credito o limite di spesa finiti presso il fornitore (ADR 0151): la frase per il titolare.
   * Finché c'è, nessun passo sta davvero lavorando e la pagina non mostra attese; null quando il motore risponde.
   */
  motore_fermo?: string | null;
  /**
   * Dove è arrivata la persona: i passi dopo `raggiunto` non si aprono finché non ci arriva con «Avanti» (si
   * preparano comunque in background); `uscita` = ha lasciato la configurazione guidata e usa l'app liberamente.
   */
  guidata: {
    raggiunto: WizardStepKey;
    /** La persona ha finito i passi (ADR 0040): «La tua azienda» è aperta e l'app ha il suo menu. */
    finita: boolean;
    uscita: boolean;
    /** Dati dell'impresa confermati nel passo «L'impresa», con il valore confermato: se il valore cambia va riconfermato. */
    conferme: Partial<Record<CompanyKeyFact, string>>;
    /** La persona ha detto di non avere la visura sotto mano: i dati dell'impresa si cercano online e si confermano. */
    senza_visura: boolean;
    /** Quando la persona ha aperto l'ultima volta ogni passo (ADR 0052): ciò che la chat cambia dopo è una novità. */
    visti?: Partial<Record<WizardStepKey, string>>;
    /** Il giudizio della persona su ogni passo (ADR 0061): Giusto o Può andar bene. */
    esiti?: Partial<Record<WizardStepKey, StepVerdict>>;
  };
}

/*
 * Gli obiettivi del titolare (ADR 0079, 0163) sono usciti dal contratto 2.0 e da Timonio (Andrea, 26/10/2026; ADR 0171):
 * la fase 1 non chiede nulla e la priorità delle proposte è una regola uguale per ogni azienda (art. 3.37).
 */

/** Il profilo dell'azienda ricavato dal consulente (ADR 0079): dice quanto deve essere grande l'azienda ideale. */
export interface CompanyProfileDto {
  stato: 'assente' | 'in_preparazione' | 'pronto';
  voci: Array<{ campo: string; valore: string }>;
}

/**
 * Il giudizio della persona su tutto un passo (ADR 0061): «giusto» conferma le voci ancora da confermare; «va_bene» lo
 * accetta e le lascia da verificare. «Correggiamo» non è un giudizio: è un messaggio nella chat del passo.
 */
export const STEP_VERDICTS = ['giusto', 'va_bene'] as const;
export type StepVerdict = (typeof STEP_VERDICTS)[number];

/**
 * Passi della configurazione guidata (ADR 0022): la struttura portante dell'ADR 0021, dal contesto alle aree. Gli
 * impianti vengono subito dopo le attività, da cui si ricavano (ADR 0031), e il software subito dopo gli impianti;
 * Programmi e strumenti sono un passo solo, dell'azienda ideale (ADR 0039). La mappa non è un passo: la pianta viva sta
 * nella pagina «La tua azienda», il risultato della configurazione (ADR 0042). Documenti e collegamenti sono della
 * configurazione operativa: i passi finiscono con le procedure (ADR 0040).
 */
export const WIZARD_STEPS = ['visura', 'impresa', 'sedi', 'attivita', 'processi', 'impianti', 'obblighi', 'software', 'organizzazione', 'procedure', 'diagnosi', 'proposte'] as const;
export type WizardStepKey = (typeof WIZARD_STEPS)[number];

/**
 * Le due parti della configurazione guidata (ADR 0031, 0037): prima l'azienda fisica (dove lavora, cosa fa, con quale
 * hardware e software), poi come funziona (organizzazione e modi di lavorare).
 */
export const WIZARD_PARTS: ReadonlyArray<{ titolo: string; sottotitolo: string; passi: readonly WizardStepKey[] }> = [
  { titolo: 'L’azienda fisica', sottotitolo: 'Dove lavora, cosa fa, con quale hardware e software', passi: ['visura', 'impresa', 'sedi', 'attivita', 'impianti', 'software'] },
  { titolo: 'Come funziona', sottotitolo: 'Organizzazione e modi di lavorare', passi: ['organizzazione', 'procedure'] },
];

export interface WizardStepDto {
  chiave: WizardStepKey;
  n: number;
  titolo: string;
  /** Cosa si fa in questo passo, in una frase. */
  descrizione: string;
  stato: SetupStatus;
  /** Dove si è arrivati, in parole. */
  dettaglio: string;
  /**
   * Il passo non è mai stato pronto e il sistema lo sta preparando: si apre quando è pronto, per non mostrare dati a
   * metà. Un passo già pronto almeno una volta si apre subito e si aggiorna in background.
   */
  preparando: boolean;
  /** Cosa sta preparando o aggiornando, in parole (vuoto quando non lavora). */
  lavori: string[];
  /** Cosa ha cambiato la chat in questo passo da quando la persona l'ha aperto l'ultima volta (ADR 0052). */
  novita?: string[];
  /** Il lettore e il contatore del passo (ADR 0167): il giro di lavoro in corso o l'ultimo, attività per attività. */
  tempi?: StepTimelineDto | null;
  /**
   * Il titolare ha fermato il lavoro del passo (ADR 0172): il passo non dice più «si prepara», il contatore è chiuso e
   * niente riparte da solo finché non lo riprende, lo rifà o cambia fase.
   */
  fermato?: boolean;
  /** La fase di questo passo ha la consegna salvata (ADR 0174): solo allora la colonna la dice «pronta». */
  consegnata?: boolean;
}

/** Un'attività del consulente in un passo, con i suoi tempi (ADR 0167). */
export interface TimelineEntryDto {
  id: string;
  etichetta: string;
  /** L'attività di cui fa parte (es. una sede dentro «Progetto ogni sede»). */
  padre: string | null;
  inizio: string | null;
  fine: string | null;
  /** previsto = non ancora partita, con la durata dell'ultima volta se c'è. */
  esito: 'previsto' | 'in_corso' | 'fatto' | 'errore';
  previsto_s: number | null;
  /** Perché la lavorazione non è riuscita, in parole semplici (es. «la risposta si è interrotta perché troppo lunga»). */
  causa?: string | null;
}

/** Un giro di lavoro di un passo (ADR 0167): da quando inizia a quando non resta nulla di aperto. */
export interface StepTimelineDto {
  inizio: string;
  fine: string | null;
  /** Il tempo massimo del contratto (art. 10.1 e 10.3): 3 minuti per la fase 0; 5 minuti con una sede, più 2 per ogni sede in più. */
  massimo_s: number;
  /**
   * Da quando conta il totale (ADR 0171): per la fase 0 dalla conferma dell'identità (art. 10.1), le righe prima restano
   * visibili con il loro tempo; null = dall'inizio del giro.
   */
  conteggio_da?: string | null;
  voci: TimelineEntryDto[];
}

/** Ricerca online dell'impresa (ADR 0021-0022): stato dell'ultima e di quella in corso. */
/** Un sito usato dalla ricerca online (ADR 0045). */
export interface ResearchSourceDto {
  dominio: string;
  /** Una pagina di esempio, da aprire. */
  url: string;
  /** Perché vale, in parole semplici (es. «riporta la vostra partita IVA»). */
  perche: string;
  /** Sedi per cui è stato usato (id); vuoto = l'impresa. */
  sedi: string[];
}

/** La persona dice che un dominio non è dell'azienda, o lo rimette (ADR 0045). */
export const ResearchDomainSchema = z.object({ dominio: z.string().trim().min(3).max(255) });
export type ResearchDomainInput = z.infer<typeof ResearchDomainSchema>;

export interface ResearchState {
  in_corso: boolean;
  eseguita_il: string | null;
  /** Pagine verificate lette (con la partita IVA, o del sito dell'azienda). */
  pagine: number;
  sito: string | null;
  trovati: { campi: number; cariche: number; attivita: number; sedi: number; ruoli: number } | null;
  errore: string | null;
  /** Sedi attive non ancora cercate online una per una (ADR 0044): la ricerca le riprende da sola. */
  sedi_da_cercare: number;
  /** Dove ha cercato (ADR 0045): i siti usati, perché valgono e per quali sedi; quelli esclusi dalla persona. */
  fonti: ResearchSourceDto[];
  domini_esclusi: string[];
  activity: ActivityDto | null;
}

/**
 * Preparazione di un'azienda appena creata (ADR 0026, 0129): i passi prima della configurazione. `identita` = il
 * titolare conferma l'impresa trovata dalla ricerca gratuita sul Registro Imprese, prima di comprare la visura; la
 * ricerca online parte dopo la lettura della visura e ne usa l'atto. `vies` e `appalti` sono le fonti pubbliche
 * ufficiali interrogate dalla partita IVA (ADR 0109): VIES della Commissione europea e la Banca dati nazionale dei
 * contratti pubblici di ANAC. `marchio` = logo e colori dal sito ufficiale (ADR 0117). `bilancio` = gli ultimi bilanci
 * depositati tramite API (Openapi); se non si può, i numeri vengono dalla ricerca online con la fonte (ADR 0155).
 */
export const PREPARATION_STEPS = ['identita', 'visura', 'lettura', 'bilancio', 'vies', 'appalti', 'marchio', 'ricerca'] as const;
export type PreparationStepKey = (typeof PREPARATION_STEPS)[number];
export type PreparationStepStatus = 'in_attesa' | 'in_corso' | 'fatto' | 'saltato' | 'errore';

export interface PreparationStep {
  passo: PreparationStepKey;
  stato: PreparationStepStatus;
  /** Cosa ha trovato o perché è saltato, in una frase. */
  nota: string | null;
  /** Quando il passo è partito (ADR 0152): l'attesa conta da qui; null se non è partito o non si sa. */
  dal?: string | null;
}

export interface PreparationState {
  avviata_il: string | null;
  passi: PreparationStep[];
  /** Ci sono i dati minimi (ragione sociale, sede legale, attività prevalente) o non arriveranno: la pagina si apre da sola. */
  pronta: boolean;
  /** Tutti i passi conclusi. */
  conclusa: boolean;
  /** L'impresa trovata sul Registro Imprese da confermare con un tocco («Sei X di Y?»); null se non c'è nulla da chiedere. */
  conferma: { denominazione: string | null; comune: string | null } | null;
  /** L'atto della visura è letto, o non arriverà: i dati trovati si possono mostrare senza che cambino dopo. */
  atto_pronto: boolean;
  /** Il motore AI è fermo per credito o limite di spesa finiti (ADR 0151): la frase per il titolare; null se risponde. */
  motore_fermo?: string | null;
  /** Il lettore e il contatore della fase 0 (art. 10.4; ADR 0171): il giro di lavoro in corso o l'ultimo. */
  tempi?: StepTimelineDto | null;
  /**
   * La verifica della partita IVA (art. 5.5; ADR 0171): com'è nel Registro Imprese; la verifica dell'Agenzia delle Entrate
   * è una fonte «da collegare» (chiede un codice di controllo a ogni lettura) e lo si dice.
   */
  verifica?: {
    registro: { iscritta: boolean; rea: string | null } | null;
    agenzia_entrate: 'da_collegare';
    vies: 'attiva' | 'non_abilitata' | null;
  };
  /** La ricerca del sito ufficiale, che parte mentre si aspetta il sì (art. 8, fase 0): in corso o finita, con le pagine. */
  sito?: { in_corso: boolean; dal: string | null; pagine: number; sito: string | null } | null;
  /** Il titolare ha detto «No, non siamo noi»: l'incarico riparte dalla partita IVA giusta (art. 7-bis.1). */
  rifiutata?: boolean;
  /** Cosa si sa già, per mostrarlo mentre si prepara. */
  trovati: {
    ragione_sociale: string | null;
    forma_giuridica: string | null;
    ateco: string | null;
    sede_legale: string | null;
    costituita_il: string | null;
    persone: Array<{ nome: string; carica: string; quota: string | null }>;
    /** La partita IVA su VIES (ADR 0109); null se non ancora interrogato. */
    vies: ViesCheckDto | null;
    /** Gli appalti pubblici vinti, dalla banca dati ANAC (ADR 0109); null se non ancora cercati. */
    appalti: PublicContractsDto | null;
  };
}

/**
 * Le ultime scoperte sull'azienda (ADR 0152): ciò che il sistema ha già salvato, con la fonte, da mostrare mentre si
 * aspetta (preparazione, lettura dell'atto, passi in costruzione). Ogni voce è un fatto letto da una fonte (visura,
 * registri, sito ufficiale, libreria), mai un testo scritto per riempire l'attesa.
 */
export const DISCOVERY_KINDS = ['impresa', 'sede', 'addetti', 'carica', 'socio', 'ateco', 'certificazione', 'sito', 'pagina', 'marchio', 'registro', 'appalti', 'libreria'] as const;
export type DiscoveryKind = (typeof DISCOVERY_KINDS)[number];

export interface DiscoveryDto {
  /** Stabile tra una lettura e l'altra: l'app riconosce le voci nuove. */
  chiave: string;
  tipo: DiscoveryKind;
  testo: string;
  /** Da dove viene, come la legge una persona (es. «Visura camerale», «Sito ufficiale»). */
  fonte: string;
  /** Il grado della fonte (art. 3.51; ADR 0171): A ufficiale, B dell'azienda, C terza parte, D aperta. */
  grado?: 'A' | 'B' | 'C' | 'D' | 'T' | null;
  url: string | null;
  /** Quando è stato salvato o letto; null se la fonte non lo dice (allora vale quando l'app lo vede comparire). */
  quando: string | null;
}

export interface DiscoveriesDto {
  scoperte: DiscoveryDto[];
  /** Quante sono in tutto (l'elenco porta solo le più recenti). */
  totale: number;
  /**
   * Il lavoro del motore per l'azienda in questo momento: cosa sta facendo e da quanti secondi, le ultime letture dei
   * suoi strumenti (quale strumento, su cosa) e l'ultima attività. Dice a chi aspetta che il lavoro va avanti.
   */
  lavoro: {
    in_corso: Array<{ cosa: string; da_secondi: number }>;
    letture: Array<{ quando: string; cosa: string }>;
    ultimo_il: string | null;
  };
}

/** La risposta del titolare a «Sei X di Y?» (ADR 0129). */
export const PreparationConfirmSchema = z.object({ si: z.boolean() });
export type PreparationConfirmInput = z.infer<typeof PreparationConfirmSchema>;

/** Esito di VIES: valida (attiva per gli scambi intracomunitari), non valida, o il servizio non rispondeva. */
export interface ViesCheckDto {
  esito: 'valida' | 'non_valida' | 'non_disponibile';
  denominazione: string | null;
  indirizzo: string | null;
  /** Quando VIES ha risposto (o l'ultimo tentativo). */
  verificata_il: string;
  fonte: string;
  url: string;
}

export interface PublicContractDto {
  cig: string;
  ruolo: string | null;
  oggetto: string | null;
  stazione_appaltante: string | null;
  cf_stazione_appaltante: string | null;
  importo: number | null;
  cpv: string | null;
  cpv_descrizione: string | null;
  data: string | null;
}

export interface PublicContractsDto {
  totale: number;
  importo_totale: number | null;
  /** I più recenti (fino a 30). */
  ultimi: PublicContractDto[];
  stazioni_principali: Array<{ nome: string; appalti: number; importo: number }>;
  /** Dati ANAC dal… (anno) e data dell'indice usato. */
  dal_anno: number;
  aggiornato_al: string;
  fonte: string;
  licenza: string;
  url: string;
}

/**
 * Guida di un passo: il progetto ideale di quella parte dell'azienda (ADR 0024), cosa il sistema ha già trovato e da
 * dove, cosa manca.
 */
export interface StepGuideDto {
  passo: WizardStepKey;
  /** null per i passi che non progettano nulla. */
  ideale: IdealDesignDto | null;
  trovati: Array<{ cosa: string; fonte: string }>;
  mancano: string[];
  aggiornato: string | null;
  /** La guida si sta (ri)scrivendo: la pagina si aggiorna da sola. */
  generating: boolean;
  /** I dati da cui nasce sono cambiati dall'ultima guida. */
  da_aggiornare: boolean;
  error: string | null;
}

/** Una voce del progetto ideale (ADR 0024): un ruolo, una funzione, un requisito, una procedura… */
/**
 * Perché una voce c'è (ADR 0087, 0146): lo chiede la legge, è buona prassi del settore, è per crescere (buona prassi che fa crescere),
 * oppure è un fatto dell'azienda trovato online (solo con la pagina in `fonti`). Se è anche un obbligo, vince la legge.
 */
export const ITEM_BASES = ['legge', 'prassi', 'crescere', 'web'] as const;
export type ItemBase = (typeof ITEM_BASES)[number];

/** Un controllo di una procedura (ADR 0133): il risultato da verificare e il codice del risultato atteso dell'Atlante. */
export interface IdealCheckDto {
  controllo: string;
  codice: string | null;
}

/** Una pagina da cui nasce una voce (ADR 0146): il titolo come lo si legge e l'indirizzo. */
export interface IdealSourceDto {
  titolo: string;
  url: string;
}

export interface IdealItemDto {
  /** Perché c'è (ADR 0087), con il riferimento: la norma, la fonte della buona prassi, l'obiettivo del titolare. */
  base?: ItemBase | null;
  riferimento?: string | null;
  /**
   * La voce standard sotto il nome vero (ADR 0146), con il riferimento breve: «Organo amministrativo · art. 2475 c.c.».
   * Il nome resta quello che usa l'azienda.
   */
  standard?: string | null;
  /** Le pagine da cui nasce la voce (ADR 0146), con il collegamento: obbligatorie per l'etichetta «Dal web». */
  fonti?: IdealSourceDto[] | null;
  /** Le righe del fascicolo da cui nasce la voce (una sede, un'attività, l'atto, una decisione, il profilo). */
  da?: string[] | null;
  /** Le voci della libreria da cui nasce (ADR 0130), solo quelle che esistono nel catalogo. */
  libreria?: LibraryRefDto[] | null;
  /** I conti del bilancio da cui nasce (ADR 0132), con l'importo: il numero sta accanto alla voce. */
  conti?: AccountRefDto[] | null;
  /** Perché vale (ADR 0130): un riferimento della libreria, una norma verificata, un fatto dell'azienda, o nessuna base. */
  fondata?: LibraryGround | null;
  nome: string;
  /** Raggruppamento scritto dal motore (es. la funzione di un ruolo). */
  gruppo: string | null;
  descrizione: string;
  dettagli: string[];
  /** Quantità, es. "1" o "2-3" persone, o una cadenza. */
  quantita: string | null;
  /** Voce (o organo) a cui riporta: le voci con riporta_a si disegnano come organigramma. */
  riporta_a: string | null;
  /** Il fatto dell'azienda o la norma che la fa scattare. */
  perche: string;
  /** Solo «Organizzazione e ruoli» (ADR 0036): organo, posto di lavoro, incarico di chi ha già un ruolo, studio esterno. */
  natura?: IdealNature | null;
  /** Figura di staff, fuori dalla linea gerarchica (sicurezza, consulenti, privacy). */
  staff?: boolean | null;
  /** Imposta da una norma. */
  obbligatorio?: boolean | null;
  /**
   * Solo «Procedure» (ADR 0054): in primo piano, con il criterio scelto dal motore tra le più usate, le più critiche e
   * quelle di valore aggiunto; assente per le altre, che si vedono con «Vedi tutte».
   */
  famiglia?: IdealProcedureFamily | null;
  /** Perché la procedura è in primo piano, in una frase. */
  rilievo?: string | null;
  /** Solo «Procedure» (ADR 0038): ogni quanto si svolge, in una parola. La cadenza per esteso resta in quantita. */
  ritmo?: IdealRhythm | null;
  /**
   * Solo «Procedure» (ADR 0137): i ruoli dell'organigramma che ne svolgono i passi, con il nome esatto; chi ne risponde
   * è in riporta_a. Il server tiene solo i ruoli che l'organigramma ha.
   */
  svolge?: string[] | null;
  /** Solo «Procedure»: cosa lascia ogni volta (documento, registro, ricevuta). */
  prova?: string | null;
  /** «Procedure» e «Programmi e strumenti»: il riferimento breve della norma che la impone, per il badge. */
  norma?: string | null;
  /** Solo «Programmi e strumenti» (ADR 0039): le voci da cui lo strumento riceve dati, con il dato. */
  riceve?: IdealFlowDto[] | null;
  /** Solo «Programmi e strumenti»: le voci a cui passa dati, con il dato. */
  passa?: IdealFlowDto[] | null;
  /**
   * «Organizzazione e ruoli» (ADR 0039): gli strumenti dell'azienda ideale con cui lavora il ruolo e di quali
   * risponde; ogni strumento ha un solo ruolo che ne risponde. «Procedure» (ADR 0054): gli strumenti con cui si svolge
   * la procedura, e risponde = quello in cui resta ciò che lascia.
   */
  strumenti?: IdealToolUseDto[] | null;
  /** Solo «Programmi e strumenti» (ADR 0049): due-quattro lettere per l'icona, scelte dal motore. */
  sigla?: string | null;
  /** Solo «Programmi e strumenti»: le fasi dell'attività in cui serve, con i loro nomi. */
  fasi?: string[] | null;
  /** Solo «Programmi e strumenti»: gli impianti o i fatti della sede per cui c'è. */
  per?: string[] | null;
  /**
   * Solo «Programmi e strumenti»: i prodotti veri più probabili per questa azienda, dal più probabile, con produttore,
   * perché e fonte (ADR 0137; prima i più usati nel settore, ADR 0049). Un riferimento, non un consiglio.
   */
  diffusi?: IdealProductDto[] | null;
  /** Solo «Programmi e servizi online» (ADR 0092): il lavoro che fa, come lo direbbe il titolare. */
  lavoro?: string | null;
  /** Il nome vero del programma che l'azienda usa già, solo con la fonte che lo mostra. */
  usate?: string | null;
  fonte_usate?: string | null;
  /** Il servizio dell'ente pubblico con cui la legge impone di farlo (es. il portale di un ministero). */
  servizio?: string | null;
  /** Contratto n. 1, fase 7: per una piattaforma pubblica, cosa serve per entrare, chi lo fa con delega, la sanzione. */
  accesso?: string | null;
  delega?: string | null;
  sanzione?: string | null;
  /** Contratto n. 1, fase 5: cosa arriva a una fase e cosa consegna. */
  entra?: string | null;
  esce?: string | null;
  /**
   * Solo «Organizzazione e ruoli» (ADR 0051): le cariche in essere (identificativi) che la voce rappresenta, scelte dal
   * cervello; la pagina ne mostra chi la ricopre, la fonte e i poteri.
   */
  cariche?: string[] | null;
  /**
   * Solo «Organizzazione e ruoli» (ADR 0131): gli standard della libreria per il ruolo o l'incarico, scelti dal cervello e
   * controllati dal server. La professione ISTAT CP2021 e il mestiere europeo ESCO, con il nome ufficiale.
   */
  professione?: IdealCodeDto | null;
  mestiere?: IdealCodeDto | null;
  /** Il CCNL dal codice CNEL e il livello di inquadramento (null se la libreria non ha i livelli di quel contratto). */
  contratto?: IdealContractDto | null;
  /** La classe di rischio dell'Accordo Stato-Regioni per chi svolge il ruolo. */
  rischio?: IdealRisk | null;
  /** I corsi di sicurezza che servono al ruolo, con le ore e il riferimento. */
  formazione?: IdealTrainingDto[] | null;
  /** Le abilitazioni, patentini e iscrizioni che il ruolo richiede, con la norma. */
  abilitazioni?: IdealLicenceDto[] | null;
  /** Gli impianti e le attrezzature della sede con cui lavora, con il nome del passo «Impianti». */
  impianti?: string[] | null;
  /** Ciò che il server ha scartato dalla scheda perché la libreria non lo ha (ADR 0131), da verificare. */
  verifica?: string[] | null;
  /** «Sedi», «Attività» e «Procedure» (ADR 0133): l'ente verso cui vale l'obbligo o l'adempimento, o che lo controlla. */
  ente?: string | null;
  /** «Attività» e «Procedure» (ADR 0133): l'area di attività dell'Atlante del Lavoro da cui nasce, controllata dal server. */
  area_atlante?: IdealCodeDto | null;
  /** Solo «Procedure» (ADR 0133): come si vede che è andata bene, con il codice del risultato atteso dell'Atlante se c'è. */
  controlli?: IdealCheckDto[] | null;
  /** Aggiunta dal titolare (ADR 0098): resta con il suo nome a ogni riprogetto, il cervello la completa e la collega. */
  aggiunta?: boolean | null;
}

/**
 * Una voce che il titolare ha aggiunto a mano a un passo progettato dal cervello (ADR 0098): programmi, procedure, ruoli.
 * È sua: il cervello non la toglie né la rinomina, la completa subito e la collega al resto quando il passo si riprogetta.
 */
export interface AddedItemDto {
  id: string;
  nome: string;
  /** Ciò che ha scritto il titolare, se ha scritto qualcosa. */
  nota: string | null;
  /** Il cervello la sta completando. */
  completando: boolean;
  /** Il completamento non è riuscito: la voce resta com'è e si completa al prossimo riprogetto. */
  errore: string | null;
}

/**
 * «Sono la stessa cosa?» (ADR 0100): due voci che sembrano la stessa e una è del titolare. Il sistema non le unisce da
 * solo: chiede, e la risposta vale per sempre.
 */
export interface SamePairDto {
  id: string;
  passo: WizardStepKey;
  tipo: 'sede' | 'attivita' | 'impianto' | 'voce';
  /** La voce del titolare (o la prima delle due, se lo sono entrambe). */
  a: { id: string; nome: string };
  b: { id: string; nome: string };
  /** Perché sembrano la stessa, in una frase. */
  perche: string;
}

export const ResolveSameSchema = z.object({ stessa: z.boolean() });

/** I passi dove si aggiunge una voce al progetto del cervello (ADR 0098). */
export const ADDABLE_STEPS = ['software', 'organizzazione', 'procedure'] as const;
export type AddableStep = (typeof ADDABLE_STEPS)[number];

export const AddItemSchema = z.object({
  nome: z.string().trim().min(2).max(200),
  nota: z.string().trim().max(1000).nullish(),
});
export type AddItemInput = z.infer<typeof AddItemSchema>;

/**
 * Un prodotto vero per un software astratto (ADR 0049, 0137): il nome commerciale di oggi, con la fonte che lo mostra. Dal
 * 0137 sono i prodotti più probabili per questa azienda, dal più probabile, con il produttore e il perché in una frase.
 */
export interface IdealProductDto {
  nome: string;
  fonte: string;
  produttore?: string | null;
  perche?: string | null;
}

/** Un codice di una classificazione della libreria con il suo nome ufficiale (ADR 0131). */
export interface IdealCodeDto {
  codice: string;
  nome: string;
}

/** Il contratto collettivo di un ruolo (ADR 0131): codice CNEL, nome e livello di inquadramento. */
export interface IdealContractDto extends IdealCodeDto {
  livello: string | null;
}

export const IDEAL_RISKS = ['basso', 'medio', 'alto'] as const;
export type IdealRisk = (typeof IDEAL_RISKS)[number];

/** Un corso di sicurezza del ruolo (ADR 0131): il corso, le ore minime e il riferimento dell'Accordo. */
export interface IdealTrainingDto {
  corso: string;
  ore: number | null;
  norma: string | null;
}

/** Un'abilitazione del ruolo (ADR 0131): cosa serve e la norma che la chiede. */
export interface IdealLicenceDto {
  nome: string;
  norma: string | null;
}

export interface IdealToolUseDto {
  nome: string;
  risponde: boolean;
}

/** Un dato che passa tra due strumenti dell'azienda ideale (ADR 0039): l'altra voce, per nome, e cosa passa. */
export interface IdealFlowDto {
  voce: string;
  dato: string;
}

/** Il ritmo di una procedura ideale (ADR 0038), dal più frequente; «evento» quando succede qualcosa, «continuo» sempre aperta. */
export const IDEAL_RHYTHMS = ['giorno', 'settimana', 'mese', 'trimestre', 'semestre', 'anno', 'evento', 'continuo'] as const;
export type IdealRhythm = (typeof IDEAL_RHYTHMS)[number];

/** Una domanda del cervello al titolare (ADR 0055): solo ciò che sa soltanto lui, con le risposte possibili. */
export interface IdealQuestionDto {
  domanda: string;
  perche: string;
  risposte: string[];
  /** La risposta che il cervello ritiene più probabile, una di `risposte` (ADR 0061): vale se il titolare non risponde. */
  probabile?: string;
  /** Cosa cambia nel progetto con ogni risposta, nello stesso ordine di `risposte` (ADR 0080). */
  effetti?: string[];
  /** La domanda riguarda la legge: viene prima delle altre (ADR 0080). */
  legge?: boolean;
  /** Una scelta del consulente (ADR 0134): l'ha decisa lui con la risposta probabile; il titolare la vede già decisa e la corregge a parole. */
  scelta?: true;
  /** Le sedi di cui parla (id): se una sede viene unita a un'altra o chiusa, la domanda non si fa più. */
  sedi?: string[];
}

/** I quattro errori (ADR 0134, 0146): di fatto (anche l'omissione), di identità, di legge, di peso. */
export const IDEAL_ERROR_KINDS = ['fatto', 'identita', 'legge', 'peso'] as const;
export type IdealErrorKind = (typeof IDEAL_ERROR_KINDS)[number];

/**
 * Un errore che il revisore ha trovato e il cervello ha corretto prima che il titolare vedesse il passo (ADR 0146):
 * diventa una riga di «Cosa ho evitato».
 */
export interface IdealAvoidedDto {
  tipo: IdealErrorKind;
  /** Cosa sarebbe stato sbagliato, in una frase. */
  cosa: string;
  /** Il fatto che lo mostra, con la fonte. */
  perche: string;
  passo: WizardStepKey;
}

/** Una riga del confronto con la visura (ADR 0146): cosa dice il web, cosa decide la visura, cosa cambia nel progetto. */
export interface VisuraComparisonRowDto {
  cosa: string;
  dal_web: string;
  visura: string;
  cambia: string;
}

/** Il fondo della configurazione (ADR 0146): il confronto con la visura e «Cosa ho evitato», da tutti i passi. */
export interface ConfigurationReviewDto {
  confronto: VisuraComparisonRowDto[];
  evitati: IdealAvoidedDto[];
  generating: boolean;
  error: string | null;
}

/** Ciò che nei passi precedenti non torna (ADR 0055): il passo e cosa. */
export interface IdealReviewDto {
  passo: WizardStepKey;
  cosa: string;
}

/** Le famiglie delle procedure ideali (ADR 0054): le più usate, le più critiche, quelle che danno valore aggiunto. */
export const IDEAL_PROCEDURE_FAMILIES = ['uso', 'critica', 'valore'] as const;
export type IdealProcedureFamily = (typeof IDEAL_PROCEDURE_FAMILIES)[number];

export const IDEAL_NATURES = ['organo', 'ruolo', 'incarico', 'esterno'] as const;
export type IdealNature = (typeof IDEAL_NATURES)[number];

export interface IdealPartDto {
  voci: IdealItemDto[];
  /** Ipotesi e fatti mancanti (es. addetti non noti). */
  note: string[];
  /** Le pagine che il cervello ha consultato con la ricerca sul web (ADR 0050), scritte dal server. */
  fonti?: string[];
}

export interface IdealPartState {
  generating: boolean;
  error: string | null;
}

export interface IdealActivityDto extends IdealPartDto {
  attivita: string;
  /** Come dovrebbe svolgersi idealmente quell'attività in quel luogo. */
  come_si_svolge: string;
}

export interface IdealSiteDto extends IdealPartState {
  sede_id: string;
  sede: string;
  luogo: string | null;
  addetti: number | null;
  /** Nessuna attività economica indicata per la sede. */
  senza_attivita: boolean;
  /** Ciò che serve alla sede in sé; null se il passo non lo progetta o non è ancora pronto. */
  sede_in_se: IdealPartDto | null;
  attivita: IdealActivityDto[];
  /** Le pagine che il cervello ha consultato per questa sede (ADR 0050). */
  fonti?: string[];
}

/**
 * Progetto ideale di un passo (ADR 0024): l'impresa, poi ogni sede con le sue attività. È un riferimento ragionato
 * dal sistema, non un dato dell'azienda.
 */
export interface IdealDesignDto {
  /** Cosa progetta il passo (es. "Organigramma e funzionigramma"). */
  titolo: string;
  /**
   * Il paragrafo in testa al passo (ADR 0146), scritto dal cervello: rispettoso e gratificante, con i numeri veri
   * dell'azienda; il server lo tiene solo se ogni cifra si ritrova nel fascicolo. Null finché non c'è.
   */
  apertura?: string | null;
  /** Gli errori che il revisore ha trovato e corretto in questo passo (ADR 0146), per «Cosa ho evitato». */
  evitati?: IdealAvoidedDto[];
  impresa: (IdealPartDto & IdealPartState) | null;
  sedi: IdealSiteDto[];
  aggiornato: string | null;
  /** Solo «Procedure» (ADR 0054): la scelta di ciò che va in primo piano, fatta dopo il progetto. */
  primo_piano?: { generating: boolean; error: string | null };
  /** Il cervello con strumenti (ADR 0055): ciò che chiede al titolare, al massimo tre domande. */
  domande?: IdealQuestionDto[];
  /** Ciò che nei passi precedenti non torna, con il passo da rivedere (ADR 0055). */
  da_rivedere?: IdealReviewDto[];
  /** Cosa è cambiato rispetto al progetto precedente, in una riga (ADR 0055). */
  cambiato?: string | null;
  /**
   * Solo «Organizzazione e ruoli» (ADR 0131): perché le persone che servono si allontanano dagli addetti noti, scritto
   * dal cervello; null se tornano o se non l'ha scritto (allora la pagina lo dice).
   */
  persone_perche?: string | null;
  /** Il secondo tempo del passo (ADR 0049) è in corso: i programmi più usati arrivano da soli, senza fermare la pagina. */
  cercando?: boolean;
  /** Quante voci del passo nascono da voci della libreria, divise per fonte (ADR 0130). */
  copertura?: LibraryCoverageDto;
}

/** Chat di approfondimento di un'area. */
export interface AreaChatState {
  area: AreaSummaryDto;
  card: ReviewCard | null;
  candidates: ProcessCandidateDto[];
  messages: OnboardingMessageDto[];
  thinking: boolean;
  activity: ActivityDto | null;
  last_error: string | null;
}

export interface OnboardingState {
  company: { name: string; vat_number: string | null };
  facts: CompanyFact[];
  themes: CompanyTheme[];
  obligations: CompanyObligation[];
  roles: CompanyRole[];
  sources: string[];
  completeness: number;
  messages: OnboardingMessageDto[];
  candidates: ProcessCandidateDto[];
  documents: OnboardingDocumentDto[];
  /** Documenti utili per questa azienda (ricavati dal settore), con indicazione se sono già arrivati. */
  recommended_documents: Array<{ titolo: string; perche: string; caricato: boolean }>;
  /** Aree della pagina Procedure (vuoto finché la mappa non è pronta). */
  areas: AreaSummaryDto[];
  /** Il motore sta lavorando (ricerca o risposta): la pagina si aggiorna da sola. */
  thinking: boolean;
  /** Il passo che si sta riprogettando dopo una decisione del titolare in «Parliamone» (ADR 0082), o null. */
  riprogetto: WizardStepKey | null;
  /**
   * Fonti personali dell'utente (posta, file del computer, cloud…) che il suo assistente AI può leggere per conoscere
   * l'azienda: null finché non gliel'ha chiesto.
   */
  user_sources: UserSources | null;
  /** Il motore sta costruendo la mappa completa dei processi (per aree). */
  mapping: boolean;
  /** Cosa sta facendo il sistema adesso, passo per passo (per la conversazione e per la mappa). */
  activity: { turn: ActivityDto | null; map: ActivityDto | null; replan: ActivityDto | null };
  map: {
    transversal: Array<{ tema: string; perche: string }>;
    sector: string | null;
    generated_at: string | null;
    areas: string[];
    error: string | null;
  };
  last_error: string | null;
}

/**
 * Informazioni sull'azienda portate da un assistente AI: dette dalla persona (conversazione) o lette in un suo
 * documento (documento, con il nome). Mai dal web: per i dati dell'azienda il web vale solo con la partita IVA.
 */
export const AddCompanyFactsSchema = z.object({
  fatti: z
    .array(
      z
        .object({
          sezione: z.string().trim().min(1).max(80).describe('Sezione della conoscenza (riusa quelle esistenti)'),
          voce: z.string().trim().min(1).max(160),
          valore: z.string().trim().min(1).max(1000),
          fonte: z.enum(['conversazione', 'documento']).describe('conversazione = detto dalla persona; documento = letto in un suo documento'),
          documento: z.string().trim().max(200).optional().describe('Nome del documento (obbligatorio per fonte documento)'),
        })
        .refine((f) => f.fonte !== 'documento' || !!f.documento, { message: 'Per fonte documento serve il nome del documento', path: ['documento'] }),
    )
    .max(50)
    .default([]),
  /** Informazioni sbagliate o non più vere, da togliere. */
  rimuovi: z
    .array(z.object({ sezione: z.string().trim().min(1).max(80), voce: z.string().trim().min(1).max(160) }))
    .max(50)
    .default([]),
});
export type AddCompanyFactsInput = z.infer<typeof AddCompanyFactsSchema>;

/** Come lavora un'area (programmi, archivi, a mano…), detto dalla persona: senza passare dal motore. */
export const AreaToolsInputSchema = z.object({
  modo: z.string().trim().min(1).max(300),
  completata: z.boolean().default(true),
});

/** Contratto di una procedura proposta scritto da chi verifica (es. il suo assistente AI), senza il motore. */
export const CandidateContractSchema = z.object({ source: z.string().min(10).max(100_000) });

/** Risposta dell'utente alla richiesta di leggere le sue fonti (posta, computer, cloud…) per conoscere l'azienda. */
export const UserSourcesSchema = z.object({
  autorizzate: z.array(z.string().trim().min(1).max(120)).max(20).describe('Fonti che l’utente ha autorizzato (es. "posta Gmail", "cartella Documenti/Boostar"); vuoto = nessuna'),
  nota: z.string().trim().max(500).optional().describe('Limiti o indicazioni dell’utente (es. "solo le mail del 2025", "non la cartella personale")'),
});
export type UserSources = z.infer<typeof UserSourcesSchema> & { chiesto_il: string };

/** Obblighi ipotizzati dal sistema che la persona conferma per la sua azienda, o toglie perché non la riguardano. */
export const VerifyObligationsSchema = z.object({
  conferma: z.array(z.string().min(1).max(120)).max(100).default([]),
  rimuovi: z.array(z.string().min(1).max(120)).max(100).default([]),
});

/** Avanzamento nella configurazione guidata: passo raggiunto con «Avanti», oppure uscita (o rientro). */
export const WizardMoveSchema = z.object({
  raggiunto: z.enum(WIZARD_STEPS).optional(),
  /**
   * Il titolare torna a una fase già chiusa (ADR 0172): quella fase si riapre, il suo giudizio e la sua fotografia si
   * tolgono, la configurazione non è più finita; le fasi dopo restano e si riprogettano da ciò che cambia.
   */
  torna: z.enum(WIZARD_STEPS).optional(),
  /** La persona ha finito i passi e va a «La tua azienda» (ADR 0040). */
  finita: z.boolean().optional(),
  /** Il titolare esce dal contratto per usare l'app liberamente (ADR 0172): il menu si apre, il percorso resta dov'è. */
  uscita: z.boolean().optional(),
  /** La persona conferma un dato dell'impresa così come lo vede (dopo averlo corretto, se serviva). */
  conferma: z.object({ campo: z.enum(COMPANY_KEY_FACTS), valore: z.string().min(1).max(8000) }).optional(),
  /** La persona annulla la conferma di un dato dell'impresa: torna da confermare. */
  annulla: z.enum(COMPANY_KEY_FACTS).optional(),
  /** La persona non ha la visura adesso: si va avanti con i dati trovati online. */
  senza_visura: z.boolean().optional(),
  /** La persona ha aperto questo passo: le novità della chat fin qui sono viste (ADR 0052). */
  visto: z.enum(WIZARD_STEPS).optional(),
  /** Il giudizio della persona su un passo con i tre tasti (ADR 0061); valore null lo toglie. */
  esito: z.object({ passo: z.enum(WIZARD_STEPS), valore: z.enum(STEP_VERDICTS).nullable() }).optional(),
});
export type WizardMoveInput = z.infer<typeof WizardMoveSchema>;

export const OnboardingReplySchema = z.object({
  message: z.string().trim().min(1).max(4000),
  /** Documenti appena caricati in archivio da far leggere al motore (procedure, moduli, export, contratti…). */
  documenti: z
    .array(z.object({ id: z.string().uuid(), titolo: z.string().max(200) }))
    .max(5)
    .default([]),
  /** Il passo del wizard in cui si trova la persona (ADR 0022): il motore parla di quello. */
  passo: z.enum(WIZARD_STEPS).optional(),
});

/**
 * Pianta viva dell'azienda (ADR 0034): per ogni sede i reparti, gli impianti, i programmi e i flussi tra reparti e
 * con l'esterno, da disegnare in isometria. Edifici, ambienti e forme sono un vocabolario visivo chiuso, uguale per
 * tutte le aziende: nomi e testi restano quelli dell'azienda o scritti dal motore.
 */
export const PLANT_BUILDINGS = ['uffici', 'capannone', 'negozio'] as const;
export type PlantBuilding = (typeof PLANT_BUILDINGS)[number];
export const PLANT_ROOMS = ['ufficio', 'direzione', 'officina', 'assemblaggio', 'laboratorio', 'magazzino', 'vendita', 'assistenza'] as const;
export type PlantRoom = (typeof PLANT_ROOMS)[number];
export const PLANT_SHAPES = ['quadro', 'macchina', 'sollevamento', 'aspirazione', 'serbatoio', 'banco', 'muletto', 'scaffale', 'server', 'clima', 'estintore', 'veicolo', 'generico'] as const;
export type PlantShape = (typeof PLANT_SHAPES)[number];
export const PLANT_NETWORKS = ['elettrico', 'antincendio', 'clima'] as const;
export type PlantNetwork = (typeof PLANT_NETWORKS)[number];
/** Cosa viaggia in un flusso: dà il colore. */
export const PLANT_FLOW_KINDS = ['clienti', 'documenti', 'denaro', 'regole'] as const;
export type PlantFlowKind = (typeof PLANT_FLOW_KINDS)[number];
/** Chi è un soggetto esterno: dà la forma (i consulenti sono persone, gli altri torri). */
export const PLANT_PARTIES = ['clienti', 'fornitori', 'enti', 'consulenti', 'banche', 'partner'] as const;
export type PlantParty = (typeof PLANT_PARTIES)[number];
/**
 * Cosa porta un impianto ai reparti che serve (ADR 0135): dà il colore del flusso e l'animazione dell'impianto nel
 * finale della configurazione. Lo sceglie il motore, come la sagoma.
 */
export const PLANT_SUPPLIES = ['energia', 'calore', 'freddo', 'aria', 'acqua', 'dati', 'sicurezza', 'movimento', 'lavorazione'] as const;
export type PlantSupply = (typeof PLANT_SUPPLIES)[number];

export interface PlantRoomDto {
  /** Chiave stabile nella sede (il nome normalizzato). */
  id: string;
  nome: string;
  ambiente: PlantRoom;
  /** Persone previste dal progetto dell'organizzazione, se dette. */
  persone: number | null;
  /** Di cosa si occupa, in una frase (scritto dal motore). */
  cosa: string | null;
  attivita: Array<{ nome: string; cadenza: string | null }>;
  /** I ruoli del progetto che formano il reparto. */
  ruoli: Array<{ nome: string; descrizione: string; persone: number | null }>;
  /** Programmi dell'azienda che usa (tra quelli registrati). */
  programmi: string[];
}

export interface PlantEquipmentDto {
  id: string;
  nome: string;
  tipo: string | null;
  perche: string | null;
  stato: EquipmentStatus;
  quantita: number | null;
  forma: PlantShape;
  /** Il reparto in cui si trova (id del reparto); null = tutta la sede o l'esterno. */
  reparto: string | null;
  esterno: boolean;
  /** Impianto che serve tutta la sede come rete. */
  rete: PlantNetwork | null;
  /** Cosa porta ai reparti che serve (ADR 0135); null finché il motore non l'ha detto. */
  porta: PlantSupply | null;
  /** Gli id dei reparti della sede che serve, e `regia` se manda segnali da tenere d'occhio (ADR 0135). */
  serve: string[];
  obblighi: EquipmentObligation[];
}

/** Un capo di un flusso: un reparto della sede (`r:<id>`), un soggetto esterno (`e:<n>`) o gli impianti (`impianti`). */
export interface PlantFlowDto {
  da: string;
  a: string;
  tipo: PlantFlowKind;
  cosa: string;
}

export interface PlantSiteDto {
  id: string;
  nome: string;
  luogo: string | null;
  indirizzo: string | null;
  legale: boolean;
  uso: SiteUse | null;
  addetti: number | null;
  attivita: string[];
  edificio: PlantBuilding;
  /** organizzazione = dal progetto del passo «Organizzazione e ruoli»; attivita = uno per attività, finché il progetto manca. */
  reparti_da: 'organizzazione' | 'attivita' | 'nessuno';
  reparti: PlantRoomDto[];
  /** Organi dell'impresa (consiglio, sindaci, revisore…): non sono reparti, stanno sopra la sede che li ospita. */
  organi: Array<{ nome: string; descrizione: string }>;
  impianti: PlantEquipmentDto[];
  flussi: PlantFlowDto[];
}

export interface PlantDto {
  impresa: string;
  sedi: PlantSiteDto[];
  /** Soggetti esterni con cui l'azienda lavora (clienti, fornitori, enti, consulenti, banche…), indicizzati da `e:<n>`. */
  esterni: Array<{ nome: string; cosa: string; tipo: PlantFlowKind; categoria: PlantParty }>;
  /** Programmi registrati dell'azienda, in uso. */
  programmi: string[];
  /** Reparti, flussi e disegno si stanno scrivendo: la pianta si completa da sola. */
  generating: boolean;
}

/**
 * «La tua azienda» (ADR 0042, 0090, 0171): il piano per arrivare all'azienda ideale. Due parti, in quest'ordine: ciò che
 * chiede la legge, le buone prassi del settore; dentro ogni parte la priorità dell'art. 3.37 del contratto 2.0 (prima gli
 * obblighi scoperti, poi ciò che pesa di più in ore ed euro, poi ciò che riduce un rischio).
 */
export const PLAN_FAMILIES = ['legge', 'prassi'] as const;
export type PlanFamily = (typeof PLAN_FAMILIES)[number];
/** Come il cervello decide cosa entra nel piano: gli stessi quattro assi per ogni azienda, da 0 a 100. */
export const PLAN_AXES = ['peso', 'frequenza', 'dipendenza', 'esposizione'] as const;
export type PlanAxis = (typeof PLAN_AXES)[number];
/** Sì, adesso · Più avanti · No, perché… (ADR 0090). Ciò che chiede la legge non si scarta: si sposta. */
export const PLAN_CHOICES = ['adesso', 'dopo', 'no'] as const;
/** Il calendario delle proposte adottate: entro 30, 90 o 365 giorni (ADR 0090). */
export const PLAN_WHEN = ['30', '90', '365'] as const;
export type PlanWhen = (typeof PLAN_WHEN)[number];
export type PlanChoice = (typeof PLAN_CHOICES)[number];

export interface PlanItemDto {
  /** Stabile tra un ricalcolo e l'altro: la scelta della persona resta sulla proposta. */
  id: string;
  famiglia: PlanFamily;
  /** La norma (legge) o la fonte (buona prassi) (ADR 0090). */
  riferimento: string;
  /** Le righe del fascicolo da cui nasce (una sede, un'attività, l'atto, una decisione…). */
  da: string[];
  /** Entro quando, se una norma lo dice (es. «entro 30 giorni dall'apertura», «ogni anno»). */
  scadenza: string | null;
  /** Quanto lavoro chiede: bassa, media, alta. */
  fatica: 'bassa' | 'media' | 'alta';
  /** Nel calendario: entro 30, 90 o 365 giorni, tenendo conto di cosa serve prima. */
  quando: PlanWhen;
  /** I titoli delle proposte che servono prima di questa. */
  prima: string[];
  titolo: string;
  /** Il reparto o il ruolo dell'azienda ideale che la porta a terra. */
  area: string;
  cosa_fa: string;
  perche_conta: string;
  /** Chiavi delle voci del conto economico su cui agisce. */
  voci: string[];
  /** Effetto annuo stimato in euro; null senza conto economico o quando non si stima (es. un'esposizione a sanzioni). */
  stima: { min: number; max: number } | null;
  come_stimata: string;
  assi: Record<PlanAxis, number>;
  fonti: string;
  /** Solo per le interconnessioni: i due strumenti da tenere collegati. */
  coppia: { da: string; a: string; collegati: boolean } | null;
  /** Solo per i controlli: quando avvisa, ogni quanto, a chi. */
  controllo: { soglia: string; frequenza: string; a_chi: string } | null;
  scelta: PlanChoice | null;
  /** Con «No»: perché, detto dal titolare. */
  motivo: string | null;
}

export interface PlanAccountDto {
  chiave: string;
  nome: string;
  descrizione: string;
  importo: number;
  tipo: 'ricavi' | 'costo' | 'margine';
}

export interface PlanRoleDto {
  ruolo: string;
  giornata: Array<{ quando: string; cosa: string; dettaglio: string }>;
  conosce: string;
  /** Il tipo di decisione che il sistema proporrà al ruolo: un modello dalle procedure finché l'azienda non è collegata. */
  decisione: { titolo: string; perche: string[]; guadagno: string; dati: string };
}

export interface CompanyPlanDto {
  /** La persona è arrivata alla fine della configurazione: prima la pagina non si apre e il piano non si compone. */
  pronta: boolean;
  generating: boolean;
  error: string | null;
  impresa: string;
  /** Da cosa dipende il risultato di un'azienda come questa, in una o due frasi. */
  sintesi: string | null;
  /** Cosa ha letto il cervello, in parole brevi (es. «14 impianti»). */
  letto: string[];
  /** Il conto economico dell'ultimo bilancio trovato tra i documenti e i dati dell'impresa; null se non c'è. */
  conto: { esercizio: string | null; fonte: string; ricavi: number; voci: PlanAccountDto[] } | null;
  proposte: PlanItemDto[];
  ruoli: PlanRoleDto[];
  stati: Record<'progettata' | 'collegata' | 'viva', { fatti: number; totale: number }>;
  aggiornato: string | null;
}

export const PlanChoiceSchema = z
  .object({ id: z.string().min(1).max(64), scelta: z.enum(PLAN_CHOICES).nullable(), motivo: z.string().trim().max(1000).optional() })
  .refine((x) => x.scelta !== 'no' || (x.motivo?.length ?? 0) >= 2, { message: 'Con «No» serve il perché' });
export type PlanChoiceInput = z.infer<typeof PlanChoiceSchema>;

/**
 * Marchio dell'azienda (ADR 0117): logo e colori letti dal suo sito ufficiale (quello con la partita IVA), con cui l'app
 * prende l'aspetto dell'azienda. Senza sito o senza colori riconoscibili l'app resta com'è.
 */
export interface BrandDto {
  stato: 'da_fare' | 'in_corso' | 'fatto' | 'nessun_sito' | 'errore';
  /** Il colore principale del marchio (#rrggbb); null se il sito non ne ha uno riconoscibile. */
  colore: string | null;
  /** Gli altri colori del marchio, dal più importante. */
  colori: string[];
  /** C'è un logo da scaricare con `GET /tenants/:id/brand/logo`. */
  logo: boolean;
  /** Il sito da cui vengono. */
  sito: string | null;
  /**
   * I caratteri del sito (ADR 0135): per i titoli e per il testo, con la famiglia generica di riserva. Ogni peso in
   * `pesi` è un file da scaricare con `GET /tenants/:id/brand/font/:n/:k` (n = posizione nell'elenco, k = del peso).
   */
  caratteri: BrandFontDto[];
  letto_il: string | null;
}

export interface BrandFontDto {
  ruolo: 'titoli' | 'testo';
  nome: string;
  generico: 'serif' | 'sans-serif';
  /** Pesi scaricati (400, 700…), uno per file; vuoto se il carattere non è libero e resta solo il nome. */
  pesi: number[];
}

/**
 * Il finale della configurazione (ADR 0135): ciò che la pianta non dice già. La partita IVA scritta all'inizio e la
 * posizione delle sedi sulla mappa, cercata dall'indirizzo.
 */
export interface FinaleDto {
  partita_iva: string | null;
  ragione_sociale: string | null;
  /** Per sede: latitudine e longitudine, o null se l'indirizzo non si trova. Le sedi che mancano si stanno cercando. */
  posizioni: Record<string, { lat: number; lon: number; precisione: 'indirizzo' | 'comune' } | null>;
  in_corso: boolean;
}
